import { useEffect, useRef, useState } from 'react';
import { formatMoney } from '../brand/brand';
import { QrCode } from './QrCode';
import { keptPass, refreshPass, type KeptPass } from '../lib/offlinePasses';

/**
 * The offline pass, on the voucher page. SOW TC-04: "validate a pre-cached signed
 * QR the app generated while last online."
 *
 * Fetched while online and kept (lib/offlinePasses.ts). When the shop's internet
 * or the customer's is down, the cashier's offline till scans this code - or types
 * it - and checks the signature against the key it cached. The balance written into
 * the pass is the balance when it was issued, and the till caps an offline spend,
 * so an old pass cannot be spent twice for more than the offline limit.
 */
export function OfflinePassPanel({
  voucher,
}: {
  voucher: { id: string; dealTitle: string; businessName: string };
}) {
  const [pass, setPass] = useState<KeptPass | null>(() => keptPass(voucher.id));
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  useEffect(() => {
    if (!online) return;
    let cancelled = false;

    void refreshPass(voucher).then((p) => { if (!cancelled && p !== null) setPass(p); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh per voucher and on reconnect only
  }, [voucher.id, online]);

  async function refresh() {
    setBusy(true);
    const p = await refreshPass(voucher);
    if (p !== null) setPass(p);
    setBusy(false);
  }

  /** The canvas as a PNG - the copy that outlives this tab, kept by the customer. */
  function saveImage() {
    const canvas = holder.current?.querySelector('canvas');
    if (canvas === null || canvas === undefined || pass === null) return;

    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `offline-pass-${voucher.dealTitle.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (pass === null) {
    return null;
  }

  const expires = new Date(pass.expiresAt);
  const expired = expires.getTime() <= Date.now();

  return (
    <div className="panel stack center" style={{ gap: 10 }}>
      <h2 style={{ margin: 0 }}>No signal at the counter?</h2>
      <p className="muted" style={{ margin: 0 }}>
        Show this offline pass instead. The cashier&apos;s till checks it without a connection.
      </p>

      {!online && (
        <p className="notice" role="status">You are offline. This pass was saved while you were online.</p>
      )}

      {expired ? (
        <p className="notice" role="status">This pass has expired. Reconnect to get a new one.</p>
      ) : (
        <div ref={holder}>
          <QrCode value={pass.token} size={260} label="Offline pass" />
        </div>
      )}

      <p className="faint" style={{ margin: 0 }}>
        {formatMoney(pass.balanceCents)} when issued · valid until {expires.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
      </p>

      <details style={{ width: '100%' }}>
        <summary className="faint">Till cannot scan? Type this code</summary>
        <p className="mono-sm" style={{ wordBreak: 'break-all', userSelect: 'all' }}>{pass.token}</p>
      </details>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        {!expired && (
          <button type="button" className="btn secondary" onClick={saveImage}>Save as image</button>
        )}
        {online && (
          <button type="button" className="btn secondary" disabled={busy} onClick={() => void refresh()}>
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>
    </div>
  );
}
